import { useCallback, useMemo, useState } from "react";

import {
  OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE,
  OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE,
} from "@/components/objects-list/components/ObjectsListTile";
import { PresetType } from "@/components/ui-v2/AspectRatio";
import { FILE_PICKER_CONFIG_COMMON } from "@/components/ui-v2/FilePicker";
import {
  FilePickerConfig,
  FilepickerFile,
} from "@/components/ui-v2/FilePicker/types";
import type { FileSelectPreviewFile } from "@/components/ui-v2/FileSelectPreviewList/types";
import {
  ImageEditorDialog,
  ImageEditorDialogProps,
} from "@/components/ui-v2/ImageEditor/ImageEditorDialog";
import { UploadableModality } from "@/constants/dropzone";
import { useShowSnackbar } from "@/hooks";
import type { ImageTransform } from "@/types";
import { getImageObject } from "@/utils/images";

import { FileUploader } from "../FileUploader/FileUploader";
import { Upload } from "./components/Upload";

export type ImageUploadFile = {
  src: string;
  driveFileId?: string;
  transform?: ImageTransform;
  width: number;
  height: number;
};

const DEFAULT_DROPPABLE_ACCEPTS = [
  OBJECTS_LIST_TILE_IMAGE_DRAGGABLE_TYPE,
  OBJECTS_LIST_TILE_VIDEO_DRAGGABLE_TYPE,
];

export interface ImageUploadProps {
  value: ImageUploadFile[];
  onChange: (images: ImageUploadFile[]) => void;
  modality?: UploadableModality;
  filePickerConfig?: FilePickerConfig;
  disabled?: boolean;
  disabledTooltip?: string;
  isDroppableEnabled?: boolean;
  droppableAccepts?: string[];
  droppableId?: string;
  isEditorEnabled?: boolean;
  aspectRatioPresets?: PresetType[];
  currentPreset?: PresetType;
  onPresetApplied?: (preset: PresetType) => void;
}

export const ImageUpload = (props: ImageUploadProps) => {
  const {
    value,
    onChange,
    modality = "IMAGE",
    filePickerConfig = FILE_PICKER_CONFIG_COMMON,
    disabled,
    disabledTooltip,
    isDroppableEnabled = true,
    droppableAccepts = DEFAULT_DROPPABLE_ACCEPTS,
    droppableId = "refinementFormImage",
    isEditorEnabled = true,
    aspectRatioPresets,
    currentPreset,
    onPresetApplied,
  } = props;

  const { showError } = useShowSnackbar();

  const images = value;
  const setImages = onChange;

  const [imageForEditor, setImageForEditor] = useState<ImageUploadFile | null>(
    null,
  );

  // Delete
  const handleFileDeleted = useCallback(
    (index: number) => setImages(images.filter((_, i) => i !== index)),
    [setImages, images],
  );

  //  Add
  const addFilePickerImages = useCallback(
    async (imageFiles: FilepickerFile[]) => {
      const newImages: ImageUploadFile[] = await Promise.all(
        imageFiles.map(async (file) => {
          try {
            const image = await getImageObject(file.src);
            return {
              width: image.width,
              height: image.height,
              src: file.src,
              driveFileId: file.id,
              transform: file.transform,
            };
          } catch (error) {
            console.error(error);
            showError(
              "Something went wrong uploading your image. Please try again.",
            );
            throw error;
          }
        }),
      );

      const updatedImages = [...images];

      newImages.forEach((newImage) => {
        const existingIndex = images.findIndex(
          (img) => img.driveFileId === newImage.driveFileId,
        );

        if (existingIndex >= 0) {
          updatedImages[existingIndex] = newImage;
        } else {
          updatedImages.push(newImage);
        }
      });

      setImages(updatedImages);
    },
    [setImages, images, showError],
  );

  const handleUploadFiles = useCallback(
    (files: FilepickerFile[]) => {
      const imageFiles = files.filter((file) =>
        file.contentType?.startsWith("image/"),
      );

      if (imageFiles.length > 0) {
        addFilePickerImages(imageFiles);
      }
    },
    [addFilePickerImages],
  );

  const filePickerImages = useMemo<FileSelectPreviewFile[]>(
    () =>
      images.map((image) => ({
        type: "image",
        thumbnailSrc: image.src,
        transform: image.transform,
        width: image.width,
        height: image.height,
      })),
    [images],
  );

  const handleEditFileCrop = useCallback(
    (index: number) => {
      setImageForEditor(images[index]);
    },
    [images],
  );

  const handleImageEditorApply: ImageEditorDialogProps["onApply"] = useCallback(
    (_url, transform, preset) => {
      const imageIndex = images.findIndex(
        (img) => img.driveFileId === imageForEditor?.driveFileId,
      );

      if (imageIndex !== -1) {
        const updatedImages = [...images];
        updatedImages[imageIndex] = {
          ...updatedImages[imageIndex],
          transform,
        };
        setImages(updatedImages);
      }

      if (preset) {
        onPresetApplied?.(preset);
      }

      setImageForEditor(null);
    },
    [imageForEditor, images, setImages, onPresetApplied],
  );

  const handleImageEditorClose = () => {
    setImageForEditor(null);
  };

  return (
    <>
      <Upload selected={filePickerImages.length > 0}>
        <FileUploader
          isDroppableEnabled={isDroppableEnabled}
          droppableAccepts={droppableAccepts}
          modality={modality}
          droppableId={droppableId}
          filePickerConfig={filePickerConfig}
          uploadedFiles={filePickerImages}
          onFileDeleted={handleFileDeleted}
          onUploadFiles={handleUploadFiles}
          onEditFile={isEditorEnabled ? handleEditFileCrop : undefined}
          disabled={disabled}
          disabledTooltip={disabledTooltip}
        />
      </Upload>
      {imageForEditor && (
        <ImageEditorDialog
          image={imageForEditor.src}
          transform={imageForEditor.transform}
          isOpen={Boolean(imageForEditor)}
          onClose={handleImageEditorClose}
          onApply={handleImageEditorApply}
          preset={currentPreset}
          presets={aspectRatioPresets}
        />
      )}
    </>
  );
};
